import { PageLoading, useDataProvider } from '@ui-framework/index';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { ServiceModuleProvider } from './ServiceModuleProvider';

const ServiceProviderContext = createContext(null);

export function ServiceProviderProvider({
  children,
  init = true,
  loadProfileWarnings = true,
  loadDispatch = true,
  loadSPInfo = true,
  loadUser = true,
  loadLocation = true,
  serviceProviderId: serviceProviderIdArg = null,
  locationId: locationIdArg = null,
  userId: userIdArg = null,
}) {
  const {
    serviceProviderId: serviceProviderIdParam,
    locationId: locationIdParam,
    userId: userIdParam,
  } = useParams();

  const serviceProviderId = serviceProviderIdArg || serviceProviderIdParam;
  const locationId = locationIdArg || locationIdParam;
  const userId = userIdArg || userIdParam;

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, get } = useDataProvider(provider);

  const DEFAULT_DATA = {
    profileWarnings: null,
    dispatchMethod: null,
    serviceProvider: null,
    user: null,
    errors: null,
    location: null,
    loading: true,
  };
  const [data, setData] = useState(DEFAULT_DATA);

  const fetchSP = async () => {
    const url = `${getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}`;
    const response = await get(url);
    return response;
  };
  const fetchSPProfileStatus = async () => {
    const url = `${getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}/profile-status`;
    const response = await get(url);
    return response;
  };
  const fetchUser = async () => {
    const url = `${getApiUrl()}/sxcl/v1/users/${userId}`;
    const response = await get(url);
    return response;
  };

  const fetchSPDispatchMethod = async () => {
    const url = `${getApiUrl()}/sxcl/v1/service-provider-profile/${serviceProviderId}/dispatch`;
    const response = await get(url);
    return response;
  };

  const fetchLocation = async () => {
    const url = `${getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}/locations/${locationId}`;
    const response = await get(url);
    return response;
  };

  const fakeCall = async dataToReturn => {
    return { ok: true, data: dataToReturn, errors: null };
  };

  const fetchAllData = async ({
    refreshProfileWarnings = false,
    refreshDispatch = false,
    refreshSPInfo = false,
    refreshUser = false,
    refreshLocation = false,
    refreshLoading = true,
  }) => {
    // Loading is needed to be controlled so child elements dont re-render
    setData({
      ...DEFAULT_DATA,
      loading: refreshLoading,
    });
    let currentErrors = [];
    let profileWarnings = null;
    let dispatchMethod = null;
    let serviceProvider = null;
    let user = null;
    let location = null;

    const [
      spProfileStatusResponse,
      spDispatchMethodResponse,
      spResponse,
      spUserResponse,
      locationResponse,
    ] = await Promise.all([
      refreshProfileWarnings && serviceProviderId
        ? fetchSPProfileStatus()
        : fakeCall(data?.profileWarnings),

      refreshDispatch && serviceProviderId
        ? fetchSPDispatchMethod()
        : fakeCall(data?.dispatchMethod),

      refreshSPInfo && serviceProviderId
        ? fetchSP()
        : fakeCall(data?.serviceProvider),
      refreshUser && userId ? fetchUser() : fakeCall(data?.user),
      refreshLocation && locationId && serviceProviderId
        ? fetchLocation()
        : fakeCall(data?.location),
    ]);

    if (spProfileStatusResponse?.ok) {
      profileWarnings = spProfileStatusResponse?.data;
    }
    if (spDispatchMethodResponse?.ok) {
      dispatchMethod = spDispatchMethodResponse?.data;
    }
    if (spResponse?.ok) {
      serviceProvider = spResponse?.data;
    }

    if (spUserResponse?.ok) {
      user = spUserResponse?.data;
    }
    if (locationResponse?.ok) {
      location = locationResponse?.data;
    }
    if (
      spProfileStatusResponse?.errors ||
      spDispatchMethodResponse?.errors ||
      spResponse?.errors
    ) {
      if (spResponse?.errors) {
        currentErrors = [...currentErrors, ...spResponse.errors];
      }
      if (spProfileStatusResponse?.errors) {
        currentErrors = [...currentErrors, ...spProfileStatusResponse.errors];
      }
      if (spDispatchMethodResponse?.errors) {
        currentErrors = [...currentErrors, ...spDispatchMethodResponse.errors];
      }
      if (spUserResponse?.errors) {
        currentErrors = [...currentErrors, ...spUserResponse.errors];
      }
      if (locationResponse?.errors) {
        currentErrors = [...currentErrors, ...locationResponse.errors];
      }
    }

    setData({
      profileWarnings,
      dispatchMethod,
      serviceProvider,
      errors: currentErrors,
      user,
      location,
      loading: false,
    });
  };

  useEffect(() => {
    if (init) {
      fetchAllData({
        refreshProfileWarnings: loadProfileWarnings,
        refreshDispatch: loadDispatch,
        refreshSPInfo: loadSPInfo,
        refreshUser: loadUser,
        refreshLocation: loadLocation,
      });
    } else {
      setData({
        ...DEFAULT_DATA,
        loading: false,
      });
    }
  }, []);

  const value = useMemo(
    () => ({
      dispatchMethod: data?.dispatchMethod,
      profileWarnings: data?.profileWarnings,
      serviceProvider: data?.serviceProvider,
      location: data?.location,
      user: data?.user,
      errors: data?.errors,
      serviceProviderId,
      userId,
      locationId,
      refreshData: ({
        refreshSPInfo = false,
        refreshProfileWarnings = false,
        refreshDispatch = false,
        refreshUser = false,
        refreshLocation = false,
        refreshLoading = false,
      }) =>
        fetchAllData({
          refreshSPInfo,
          refreshProfileWarnings,
          refreshDispatch,
          refreshUser,
          refreshLocation,
          refreshLoading,
        }),
    }),
    [data],
  );

  if (data?.loading) {
    return (
      <PageLoading>
        <FormattedMessage
          id="LOADING_SERVICE_PROVIDER_INFO"
          defaultMessage="Loading Service Provider Info"
        />
        ...
      </PageLoading>
    );
  }

  return (
    <ServiceProviderContext.Provider value={value}>
      {children}
    </ServiceProviderContext.Provider>
  );
}

export const useServiceProvider = () => {
  if (!ServiceProviderContext) {
    throw new Error(
      'Need to wrap useServiceProvider Hook in ServiceProviderProvider',
    );
  }
  return useContext(ServiceProviderContext);
};

export function withServiceProvider(WrappedComponent, options = {}) {
  const { init = true } = options;
  function WithServiceProviderComponent(props) {
    return (
      <ServiceProviderProvider init={init}>
        <ServiceModuleProvider>
          <WrappedComponent {...props} />
        </ServiceModuleProvider>
      </ServiceProviderProvider>
    );
  }
  WithServiceProviderComponent.displayName = `WithServiceProvider(${
    WrappedComponent?.displayName ?? WrappedComponent?.name ?? 'Component'
  })`;
  return WithServiceProviderComponent;
}
